import { Col, Input, Label, Row } from "reactstrap";
import React from "react";
import { toast } from "react-toastify";

const TabUserDetails = ({
  createUpdateData,
  setCreateUpdateData,
  activeVerticalTab,
  setActiveVerticalTab,
  isEdit,
  // Task 24454 - shortcut straight to the Summary when the later steps are
  // already answered (the "Create Another User (Same Settings)" case).
  onJumpToSummary,
  canJumpToSummary,
}) => {
  // Returns true when this step is valid. `jump` means the caller wants to
  // skip ahead rather than step forward, so it must not advance the tab itself.
  const handlePrecheckSteps = ({ jump = false } = {}) => {
    //If Email is not valid or empty toast error
    if (
      !createUpdateData?.User_Email ||
      !createUpdateData?.User_Email.includes("@")
    ) {
      toast.error("Please enter a valid Email");
      return false;
    }
    //If First Name is empty toast error
    if (!createUpdateData?.User_UserFirstName) {
      toast.error("Please enter First Name");
      return false;
    }
    //If Last Name is empty toast error
    if (!createUpdateData?.User_UserLastName) {
      toast.error("Please enter Last Name");
      return false;
    }
    const extraEmails = createUpdateData?.EmailCcExtra || [];
    const hasExtra =
      createUpdateData?.FlagEmailCcExtra === "1" &&
      extraEmails.some((email) => email.trim() !== "");
    //If additional recipients are enabled, validate any entered emails
    if (createUpdateData?.FlagEmailCcExtra === "1") {
      const invalid = extraEmails.some(
        (email) => email.trim() !== "" && !email.includes("@")
      );
      if (invalid) {
        toast.error("Please enter valid recipient email addresses");
        return false;
      }
    }
    // Task 24452 - sending NO password email is a legitimate choice: you may
    // be setting the account up ahead of someone starting, or handing the
    // credentials over in person. This used to be refused outright; the
    // consequence is now spelled out on screen instead (see the warning below)
    // so it is an informed choice rather than a blocked one.
    if (!jump) setActiveVerticalTab(activeVerticalTab + 1);
    return true;
  };
  // Nobody is going to receive the credentials email.
  const noPasswordEmailRecipients =
    !isEdit &&
    createUpdateData?.FlagEmailToUser === "0" &&
    createUpdateData?.FlagEmailCcSelf !== "1" &&
    !(
      createUpdateData?.FlagEmailCcExtra === "1" &&
      (createUpdateData?.EmailCcExtra || []).some((e) => e.trim() !== "")
    );

  return (
    <Row>
      <div>
        <h2>User Details</h2>
        <p className="text-muted mb-0">
          Their login and how the password reaches them.
        </p>
      </div>
      <hr />
      <Col lg={12} className={"p-2"}>
        <Label>
          Email <span className={"text-danger"}>*</span>
        </Label>
        <Input
          disabled={isEdit}
          type="email"
          required={true}
          placeholder="Email"
          value={
            createUpdateData?.User_Email ? createUpdateData?.User_Email : ""
          }
          onChange={(e) => {
            setCreateUpdateData({
              ...createUpdateData,
              User_Email: e.target.value,
            });
          }}
        />
      </Col>

      <Col lg={6} className={"p-2"}>
        <Label>
          First Name <span className={"text-danger"}>*</span>
        </Label>
        <Input
          required={true}
          type="text"
          placeholder="First Name"
          value={
            createUpdateData?.User_UserFirstName
              ? createUpdateData?.User_UserFirstName
              : ""
          }
          onChange={(e) => {
            setCreateUpdateData({
              ...createUpdateData,
              User_UserFirstName: e.target.value,
            });
          }}
        />
      </Col>

      <Col lg={6} className={"p-2"}>
        <Label>
          Last Name <span className={"text-danger"}>*</span>
        </Label>
        <Input
          required={true}
          type="text"
          placeholder="Last Name"
          value={
            createUpdateData?.User_UserLastName
              ? createUpdateData?.User_UserLastName
              : ""
          }
          onChange={(e) => {
            setCreateUpdateData({
              ...createUpdateData,
              User_UserLastName: e.target.value,
            });
          }}
        />
      </Col>
      {!isEdit && (
        <Col lg={12} className={"p-2"}>
          {/* Password email recipients (the generated credentials email) */}
          <div className={"mb-1 fw-bold"}>Password email recipients</div>
          <div>
              {/* Send to the new user (default on) - uncheck to send only to
                  the manager / additional recipients */}
              <div className={"form-check"}>
                <Input
                  type="checkbox"
                  className={"form-check-input"}
                  id={"flagEmailToUser"}
                  checked={createUpdateData?.FlagEmailToUser !== "0"}
                  onChange={() => {
                    setCreateUpdateData({
                      ...createUpdateData,
                      FlagEmailToUser:
                        createUpdateData?.FlagEmailToUser === "0" ? "1" : "0",
                    });
                  }}
                />
                <Label className={"form-check-label"} for={"flagEmailToUser"}>
                  Send to the new user
                </Label>
              </div>

              {/* Send a copy to the logged-in user (CC or BCC) */}
              <div className={"form-check"}>
                <Input
                  type="checkbox"
                  className={"form-check-input"}
                  id={"flagEmailCcSelf"}
                  checked={createUpdateData?.FlagEmailCcSelf === "1"}
                  onChange={() => {
                    setCreateUpdateData({
                      ...createUpdateData,
                      FlagEmailCcSelf:
                        createUpdateData?.FlagEmailCcSelf === "1" ? "0" : "1",
                    });
                  }}
                />
                <Label className={"form-check-label"} for={"flagEmailCcSelf"}>
                  Send a copy to me
                </Label>
              </div>

              {createUpdateData?.FlagEmailCcSelf === "1" && (
                <div className={"d-flex align-items-center mb-2 ms-4"}>
                  <span className={"me-3"}>Send my copy as:</span>
                  <div className={"form-check form-check-inline"}>
                    <Input
                      type="radio"
                      className={"form-check-input"}
                      id={"emailSelfModeCc"}
                      name={"emailSelfMode"}
                      checked={(createUpdateData?.EmailSelfMode || "CC") === "CC"}
                      onChange={() => {
                        setCreateUpdateData({
                          ...createUpdateData,
                          EmailSelfMode: "CC",
                        });
                      }}
                    />
                    <Label
                      className={"form-check-label"}
                      for={"emailSelfModeCc"}
                    >
                      CC
                    </Label>
                  </div>
                  <div className={"form-check form-check-inline"}>
                    <Input
                      type="radio"
                      className={"form-check-input"}
                      id={"emailSelfModeBcc"}
                      name={"emailSelfMode"}
                      checked={createUpdateData?.EmailSelfMode === "BCC"}
                      onChange={() => {
                        setCreateUpdateData({
                          ...createUpdateData,
                          EmailSelfMode: "BCC",
                        });
                      }}
                    />
                    <Label
                      className={"form-check-label"}
                      for={"emailSelfModeBcc"}
                    >
                      BCC
                    </Label>
                  </div>
                </div>
              )}

              {/* Additional recipients toggle */}
              <div className={"form-check mt-1"}>
                <Input
                  type="checkbox"
                  className={"form-check-input"}
                  id={"flagEmailCcExtra"}
                  checked={createUpdateData?.FlagEmailCcExtra === "1"}
                  onChange={() => {
                    const enabling =
                      createUpdateData?.FlagEmailCcExtra !== "1";
                    setCreateUpdateData({
                      ...createUpdateData,
                      FlagEmailCcExtra: enabling ? "1" : "0",
                      EmailCcExtra: enabling
                        ? createUpdateData?.EmailCcExtra?.length
                          ? createUpdateData.EmailCcExtra
                          : [""]
                        : [],
                    });
                  }}
                />
                <Label className={"form-check-label"} for={"flagEmailCcExtra"}>
                  Also send to additional recipients (e.g. manager, IT)
                </Label>
              </div>

              {noPasswordEmailRecipients && (
                <div className={"alert alert-warning py-2 px-3 mt-2 mb-0"}>
                  <i className="ri-information-line align-bottom me-1"></i>
                  No password email will be sent. You will see the generated
                  password on the final step — copy it then, or the new user can
                  set their own with <strong>Forgot password</strong>.
                </div>
              )}

              {createUpdateData?.FlagEmailCcExtra === "1" && (
                <div className={"mt-2"}>
                  {/* CC (default) or BCC the additional recipients */}
                  <div className={"d-flex align-items-center mb-2"}>
                    <span className={"me-3"}>Send additional recipients as:</span>
                    <div className={"form-check form-check-inline"}>
                      <Input
                        type="radio"
                        className={"form-check-input"}
                        id={"emailExtraModeCc"}
                        name={"emailExtraMode"}
                        checked={(createUpdateData?.EmailExtraMode || "CC") === "CC"}
                        onChange={() => {
                          setCreateUpdateData({
                            ...createUpdateData,
                            EmailExtraMode: "CC",
                          });
                        }}
                      />
                      <Label
                        className={"form-check-label"}
                        for={"emailExtraModeCc"}
                      >
                        CC
                      </Label>
                    </div>
                    <div className={"form-check form-check-inline"}>
                      <Input
                        type="radio"
                        className={"form-check-input"}
                        id={"emailExtraModeBcc"}
                        name={"emailExtraMode"}
                        checked={createUpdateData?.EmailExtraMode === "BCC"}
                        onChange={() => {
                          setCreateUpdateData({
                            ...createUpdateData,
                            EmailExtraMode: "BCC",
                          });
                        }}
                      />
                      <Label
                        className={"form-check-label"}
                        for={"emailExtraModeBcc"}
                      >
                        BCC
                      </Label>
                    </div>
                  </div>
                  {(createUpdateData?.EmailCcExtra || [""]).map(
                    (email, index) => (
                      <div
                        key={index}
                        className={"d-flex align-items-center mb-2"}
                      >
                        <Input
                          type="email"
                          placeholder="name@company.com"
                          value={email}
                          onChange={(e) => {
                            const next = [
                              ...(createUpdateData?.EmailCcExtra || [""]),
                            ];
                            next[index] = e.target.value;
                            setCreateUpdateData({
                              ...createUpdateData,
                              EmailCcExtra: next,
                            });
                          }}
                        />
                        <span
                          className={"btn btn-sm btn-danger ms-2"}
                          onClick={() => {
                            const next = (
                              createUpdateData?.EmailCcExtra || []
                            ).filter((_, i) => i !== index);
                            setCreateUpdateData({
                              ...createUpdateData,
                              EmailCcExtra: next.length ? next : [""],
                            });
                          }}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </span>
                      </div>
                    )
                  )}
                  <span
                    className={"btn btn-sm btn-outline-primary"}
                    onClick={() => {
                      setCreateUpdateData({
                        ...createUpdateData,
                        EmailCcExtra: [
                          ...(createUpdateData?.EmailCcExtra || []),
                          "",
                        ],
                      });
                    }}
                  >
                    <i className="fa-solid fa-plus me-1"></i> Add recipient
                  </span>
                </div>
              )}
            </div>
        </Col>
      )}
      <Col lg={12} className={"p-2"}>
        {/*    ProfileImage upload base64*/}
        <Label>Profile Image</Label>
        <Input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const reader = new FileReader();
            reader.onload = () => {
              setCreateUpdateData({
                ...createUpdateData,
                User_ImageUrl: reader.result,
              });
            };
            reader.readAsDataURL(e.target.files[0]);
          }}
        />
        {createUpdateData?.User_ImageUrl && (
          <div className={"mt-2"}>
            <hr />
            <h5>Profile Preview</h5>
            <div>
              <img
                src={createUpdateData?.User_ImageUrl}
                alt=""
                className="img-fluid rounded-circle avatar-xxs"
              />
              <span>
                {" "}
                {createUpdateData?.User_UserFirstName +
                  " " +
                  createUpdateData?.User_UserLastName}
              </span>
            </div>
          </div>
        )}
      </Col>
      <Col lg={12} className={"p-2"}>
        <div className={"d-flex justify-content-end gap-2 mt-2"}>
          {canJumpToSummary && (
            <button
              type="button"
              className={"btn btn-outline-success"}
              title="Everything else is already filled in from the last user"
              onClick={() => {
                if (handlePrecheckSteps({ jump: true })) onJumpToSummary?.();
              }}
            >
              <i className="ri-skip-forward-line align-bottom me-1" />
              Review &amp; Create
            </button>
          )}
          <button
            type="button"
            className={"btn btn-success"}
            onClick={() => {
              handlePrecheckSteps();
            }}
          >
            Next
            <i className="ri-arrow-right-line align-bottom ms-1" />
          </button>
        </div>
      </Col>
    </Row>
  );
};

export default TabUserDetails;
